import { createEnvelopeSource } from './create-envelope-source';
import type {
    KpiCardDataRequest,
    KpiCardState,
    KpiSource,
    KpiSourceDeps,
} from '../types';
import { ROAD_TO_REWARDS_CAMPAIGN_ID } from './road-to-rewards-campaign';

type ContactabilityMetric = 'pushReachable' | 'phoneVerified' | 'smsReachable';

/** Slice wording per metric, each mirroring the mockup's own copy. */
const SLICE_LABELS: Record<
    ContactabilityMetric,
    { reached: string; unreached: string }
> = {
    pushReachable: { reached: 'Active', unreached: 'Off' },
    phoneVerified: { reached: 'Verified', unreached: 'Invalid' },
    smsReachable: { reached: 'Reachable', unreached: 'Not opted in' },
};

const base = createEnvelopeSource({
    label: 'Campaign contactability',
    endpoint: 'analytics/campaign-contactability-member',
    metrics: [
        { id: 'pushReachable', label: 'Push reachable', type: 'ratio' },
        { id: 'phoneVerified', label: 'Phone verified', type: 'ratio' },
        { id: 'smsReachable', label: 'SMS reachable', type: 'ratio' },
    ],
    forms: ['pie'],
    regionGrain: 'province',
    staticParams: { campaignId: ROAD_TO_REWARDS_CAMPAIGN_ID },
    liveCapable: true,
});

/**
 * Every metric is a 0..1 ratio with no breakdown axis, so the generic `pie`
 * branch does not fit. This reads each through `rate` instead and turns its
 * numerator/denominator into the donut's two slices, in absolute members.
 */
export const campaignContactabilitySource: KpiSource<ContactabilityMetric> = {
    descriptor: base.descriptor,

    async getCardData(
        req: KpiCardDataRequest,
        deps: KpiSourceDeps
    ): Promise<KpiCardState> {
        const state = await base.getCardData({ ...req, form: 'rate' }, deps);
        if (state.status !== 'ready' || state.data.form !== 'rate') {
            return state;
        }
        const metricId = (req.metric ??
            base.descriptor.metrics[0].id) as ContactabilityMetric;
        const { reached, unreached } = SLICE_LABELS[metricId];
        const { numerator, denominator } = state.data;
        return {
            ...state,
            data: {
                form: 'pie',
                slices: [
                    { label: reached, value: numerator },
                    { label: unreached, value: denominator - numerator },
                ],
                palette: req.palette,
            },
        };
    },
};
